/**
 * Shipment options for a SINGLE item, opened from the list group header or footer.
 *
 * The group case has `Combining`, whose three radios are the outcome picker. One item never had an
 * equivalent: the grouping panel took it straight into a splitting screen, so multi-parcel was only
 * reachable from inside the item panel. This is the same shape of screen with the outcomes that
 * actually apply to one item:
 *
 *   - One box      - only when the item ALREADY has stored parcels. For an item without them this is what
 *                    it already is, so there would be nothing to choose; once it has them the customer is
 *                    editing a parcel count and one box is a real, different outcome that needs naming.
 *   - Consignment - one booking, one tracking number, several boxes.
 *   - Split       - several separate shipments, which is whichever of Splitting / Convert / Custom the
 *                   item's shape implies. Those components are unchanged and provide their own commit.
 *
 * The consignment commit PATCHES THE EXISTING ITEM. It does not go through createGroup, which for one
 * item would create a replacement with Source 'grouped', blank the original's order lines and need a
 * follow-up save to attach the consignment.
 */
import { P2GAlert, P2GBox, P2GButton, P2GRadio, P2GRadioGroup, P2GText } from '@parcel2go/core-ui';
import { useCallback, useMemo, useState } from 'react';
import {
  ConsignmentApiModel,
  Country,
  Dimensions,
  InputRuleApiModel,
  PackageApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { SidePanelSections } from '../../../../../components/sidepanel-section';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import { FilterToSendableItems } from '../panels/FilterToSendableItems';
import { consignmentParcelBound } from '~/apps/smartsend/lib/parcels/bounds';
import { getDisplayDimensions, hasStoredParcels } from '~/apps/smartsend/lib/parcels/itemView';
import { contentIsIncomplete } from '~/apps/smartsend/lib/parcels/rules';
import { useParcelPlan } from '~/apps/smartsend/lib/parcels/useParcelPlan';
import {
  GenerateConsignmentFromItem,
  RedistributeConsignmentParcels
} from '../panels/consignments/GenerateConsignmentFromItem';
import { ConsignmentBuilder } from './ConsignmentBuilder';
import { Splitting } from './Splitting';
import { Convert } from './Convert';
import { Custom } from './Custom';
import SingleItemOptionsReverted from './SingleItemOptionsReverted';
import StickyButtons from '~/components/sticky';

/** The split flows that apply to one item, as derived by GroupingPanel. */
export type SingleItemSplitFlow = 'SPLIT' | 'CONVERT' | 'CUSTOM' | '';

type Outcome = 'SINGLE_BOX' | 'CONSIGNMENT' | 'SPLIT';

export interface props {
  group: SmartSendItemGroup;
  /** Which split screen this item's shape calls for. Absent means no split option at all. */
  splitFlow: SingleItemSplitFlow;
  countries?: Country[];
  productValidation?: InputRuleApiModel[];
  tariffSystem?: string;
  getPackages: () => Promise<PackageApiModel[]>;
  /** Saves the consignment onto the existing item and closes the panel. */
  saveConsignment: (itemId: string, consignment: ConsignmentApiModel, dimensions: Dimensions) => void;
  /** The group-creating commit the Custom flow uses, unchanged. */
  createItems: (items: SmartSendItemApiModel[]) => void;
  /** The item-splitting commit Splitting and Convert use, unchanged. */
  createSplitItems: (item: SmartSendItemApiModel, newItems: SmartSendItemApiModel[], save: string[]) => void;
  cancel: () => void;
}

export const SingleItemOptions: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const item = props.group.Items[0];
  const existing = item?.Consignment;

  const [outcome, setOutcome] = useState<Outcome>('CONSIGNMENT');

  const sendableLines = useCallback(
    () => (item?.MarketplaceOrderLines ?? []).filter(FilterToSendableItems),
    [item?.MarketplaceOrderLines]
  );

  const maxParcels = useMemo(() => consignmentParcelBound(sendableLines(), existing), [sendableLines, existing]);

  // Starts from the item's OWN dimensions, unlike the group case which sums every item's weight and
  // has no meaningful L/W/H to start from.
  const [initialDims] = useState<Dimensions>(() => ({ ...item?.PackageDimensions }));

  const parcels = useParcelPlan({
    // Pre-selected at the count the item already has, so opening this on a 3-parcel item shows the
    // arrangement it has rather than resetting it.
    initialParcelCount: Math.min(Math.max(existing?.Parcels?.length || 2, 2), Math.max(maxParcels, 1)),
    initialDimensions: initialDims,
    // Redistributing preserves per-row customs edits and manually added rows; generating from the order
    // lines would throw them away. Same pair of seeds the item panel's picker uses.
    seed: (count, dims) =>
      existing
        ? RedistributeConsignmentParcels(item.Id, existing, dims, count)
        : GenerateConsignmentFromItem(item, dims, count)
  });

  const parcelCount = parcels.plan?.Parcels?.length ?? 1;

  // Only an item that HAS parcels can be sent as one box - for one without them, one box is already what
  // it is. hasStoredParcels rather than a truthiness check on Consignment, so a { Parcels: [] } consignment
  // does not offer to discard an arrangement that does not exist.
  const canSendAsSingleBox = hasStoredParcels(item);

  // One parcel means the item is a plain single-parcel shipment again, so the save clears the
  // consignment rather than storing a one-parcel one. Reachable only when canSendAsSingleBox is false:
  // otherwise the picker's 1x flips to the SINGLE_BOX outcome, which owns the clear.
  const collapsingToSingle = parcelCount <= 1;

  const confirm = () => {
    if (collapsingToSingle && !existing) {
      // Nothing to clear - saving a null over a null would only mark the item unsaved and requote it.
      props.cancel();
      return;
    }
    props.saveConsignment(item.Id, collapsingToSingle ? null : parcels.plan, parcels.dimensions);
  };

  const confirmSingleBox = () => {
    // The single box inherits the parcels' TOTAL weight, not the item's own. PackageDimensions.Weight is
    // stale or zero on a multi-parcel item - nothing maintains it once parcels exist - so saving it as-is
    // while clearing the consignment would drop the row straight into ITEM-WEIGHT the moment the customer
    // confirmed. L/W/H is left as the item has it: the boxes are gone, so their dimensions mean nothing,
    // and the real box size is an item-panel edit.
    props.saveConsignment(item.Id, null, {
      ...item.PackageDimensions,
      Weight: getDisplayDimensions(item).Weight
    });
  };

  const contentContext = useMemo(
    () => ({
      countries: props.countries,
      productValidation: props.productValidation,
      tariffSystem: props.tariffSystem,
      sender: item?.Sender,
      recipient: item?.Recipient,
      currencyCode: item?.CurrencyCode
    }),
    [props.countries, props.productValidation, props.tariffSystem, item]
  );

  // Ordered one box / consignment / split to match Combining, so the two screens read the same way round -
  // even though this one starts on the second option, because the customer arrives already in a consignment.
  const options = [
    ...(canSendAsSingleBox
      ? [
          <P2GRadio
            name="SingleItemOutcome"
            fullWidth
            value="SINGLE_BOX"
            checked={outcome === 'SINGLE_BOX'}
            id="single-item-one-box"
            onChange={() => setOutcome('SINGLE_BOX')}
            label={t('SINGLE_ITEM_ONE_BOX_RADIO', { defaultValue: 'Send everything in one box' })}
          />
        ]
      : []),
    <P2GRadio
      name="SingleItemOutcome"
      fullWidth
      value="CONSIGNMENT"
      checked={outcome === 'CONSIGNMENT'}
      id="single-item-consignment"
      onChange={() => {
        // The picker's 1x reseeded the plan to one parcel on its way out, so coming back would otherwise
        // offer a one-parcel consignment. Same bump Combining's consignment radio does.
        if (parcelCount <= 1) {
          parcels.setParcelCount(2);
        }
        setOutcome('CONSIGNMENT');
      }}
      label={t('COMBINE_AS_MULTI_PARCEL_RADIO', {
        defaultValue: 'Multiple boxes with one collection/drop-off and one tracking number'
      })}
    />,
    ...(props.splitFlow
      ? [
          <P2GRadio
            name="SingleItemOutcome"
            fullWidth
            value="SPLIT"
            checked={outcome === 'SPLIT'}
            id="single-item-split"
            onChange={() => setOutcome('SPLIT')}
            label={t('USE_MULTIPLE_SHIPMENTS_BUTTON', {
              defaultValue: 'Multiple boxes with different collection/drop-off and tracking numbers'
            })}
          />
        ]
      : [])
  ];

  return (
    <>
      <SidePanelSections>
        <P2GBox sx={{ '& .MuiFormControlLabel-root': { margin: 0 } }}>
          <P2GRadioGroup name="SingleItemOutcome" layout="column" children={options} />
        </P2GBox>
        {outcome === 'SINGLE_BOX' && <SingleItemOptionsReverted item={item} />}
        {outcome === 'CONSIGNMENT' && (
          <>
            <P2GText>
              {t('SINGLE_ITEM_MULTI_PARCEL_INTRO_TEXT', {
                defaultValue: `This stays as one booking with the courier. You'll keep one reference number and can use multiple boxes to send your items in.`
              })}
            </P2GText>
            <ConsignmentBuilder
              parcels={parcels}
              maxParcels={maxParcels}
              content={contentContext}
              getPackages={props.getPackages}
              dimensionsFallback={item?.PackageDimensions}
              // 1x means "one box", so send the customer to the outcome that describes it rather than
              // confirming a clear from in here - one route, one wording. Gated: flipping to an outcome
              // whose radio is not rendered would leave the screen with no visible selection, so an item
              // with no consignment keeps the collapsingToSingle behaviour instead.
              onCollapseToSingle={canSendAsSingleBox ? () => setOutcome('SINGLE_BOX') : undefined}
            />
          </>
        )}
        {outcome === 'SPLIT' && (
          <>
            {props.splitFlow === 'SPLIT' && (
              <Splitting
                group={props.group}
                split={(i, n) => props.createSplitItems(i, n, ['Tags', 'MarketplaceOrderLines'])}
                getPackages={props.getPackages}
                countries={props.countries}
                productValidation={props.productValidation}
                tariffSystem={props.tariffSystem}
                cancel={props.cancel}
              />
            )}
            {props.splitFlow === 'CONVERT' && (
              <Convert
                group={props.group}
                split={(i, n) => props.createSplitItems(i, n, ['MarketplaceOrderLines'])}
                countries={props.countries}
                productValidation={props.productValidation}
                tariffSystem={props.tariffSystem}
                cancel={props.cancel}
              />
            )}
            {props.splitFlow === 'CUSTOM' && (
              <Custom
                group={props.group}
                create={props.createItems}
                countries={props.countries}
                productValidation={props.productValidation}
                tariffSystem={props.tariffSystem}
                getPackages={props.getPackages}
              />
            )}
          </>
        )}
      </SidePanelSections>
      {outcome === 'SINGLE_BOX' && (
        <>
          <P2GBox mt={2}>
            <P2GAlert type="success">
              <P2GText variant="body2">
                {t('SINGLE_ITEM_SINGLE_PARCEL_SUMMARY_TEXT', {
                  defaultValue: 'This will be sent as a single-parcel shipment.'
                })}
              </P2GText>
            </P2GAlert>
          </P2GBox>
          <StickyButtons>
            <P2GBox mt={2}>
              <P2GButton variant="primary" onClick={confirmSingleBox}>
                {t('CONFIRM_BUTTON', { defaultValue: 'Confirm' })}
              </P2GButton>
            </P2GBox>
          </StickyButtons>
        </>
      )}
      {outcome === 'CONSIGNMENT' && (
        <>
          <P2GBox mt={2}>
            <P2GAlert type="success">
              <P2GText variant="body2">
                {collapsingToSingle
                  ? t('SINGLE_ITEM_SINGLE_PARCEL_SUMMARY_TEXT', {
                      defaultValue: 'This will be sent as a single-parcel shipment.'
                    })
                  : t('SINGLE_ITEM_MULTI_BOX_SUMMARY_TEXT', {
                      defaultValue: 'This will be sent as 1 shipment across {{parcelCount}} boxes.',
                      parcelCount
                    })}
              </P2GText>
            </P2GAlert>
          </P2GBox>
          <StickyButtons>
            <P2GBox mt={2}>
              {/* Weight is deliberately NOT a gate: the dimensions section is on this same screen, and a
                weightless consignment raises ITEM-WEIGHT in the row where the weight quick fix now
                reflows the parcel weights correctly. */}
              <P2GButton
                variant="primary"
                disabled={!collapsingToSingle && contentIsIncomplete(parcels.plan)}
                onClick={confirm}
              >
                {t('CONFIRM_BUTTON', { defaultValue: 'Confirm' })}
              </P2GButton>
            </P2GBox>
          </StickyButtons>
        </>
      )}
    </>
  );
};
